import { useCallback, useEffect, useMemo, useState } from "react";

import './QuotationForm.scss';
import { newQuotation } from "../../api/quotationApiClient";
import { findScenario } from "../../api/scenarioApiClient";

import { QuotationContext } from "../../contexts/QuotationContext";
import { Scenario } from "../../api/model/scenario";
import { createTheme, ThemeProvider } from "@material-ui/core";
import { SalesProgramOption } from "../../model/quoationPackage";
import { VehicleGroup, VehicleType } from "../../model/vehicle";
import { ScenarioComponent } from "../../components/Scenario";

import { strings } from "../../strings/strings";
import { ErrorMessageDialog } from "../../components/dialogs/ErrorMessageDialog";
import { ErrorDialogType } from "../../utils/hooks/useErrorDialog";
import QuotationCardContextProvider from '../../contexts/QuotationCardContext';
import { useCheckingForMemorizing } from "../../utils/hooks/memorizeMethods";

import { Skeleton } from '@material-ui/lab';
import Grid from '@material-ui/core/Grid';
import { ProductType } from "../../api/model/common";

interface QuotationFormProps {
    salesVariant: string
    scenarioCode: string
    type: ProductType
}

export const QuotationForm = (props: QuotationFormProps) => {
    const { salesVariant, scenarioCode, type } = props
    const [isLoading, setLoading] = useState(true);
    const [quotation, setQuotation] = useState(null);
    const [totalPremium, setTotalPremium] = useState(0);
    const [currentScenario, setCurrentScenario] = useState<Scenario>(null);

    const [packages, setPackages] = useState([]);

    const [selectedDeductible, setSelectedDeductible] = useState(null);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [selectedProgram, setSelectedProgram] = useState<SalesProgramOption>();
    const [selectedVtplCovered, setSelectedVtplCovered] = useState(false);
    const [selectedVtplSumInsured, setSelectedVtplSumInsured] = useState<number>();
    const [selectedVtplPremium, setSelectedVtplPremium] = useState(0);
    const [isQuotationEditable, setIsQuotationEditable] = useState(true);
    const [agreeOption1, setAgreeOption1] = useState(false);
    const [agreeOption2, setAgreeOption2] = useState(false);
    const [agreeInformOption, setAgreeInformOption] = useState(false);

    const [selectedView, setSelectedView] = useState('NEWCLIENT');
    const [vehicleTypes, setVehicleTypes] = useState<VehicleType[]>([]);
    const [vehicleGroup, setVehicleGroup] = useState<VehicleGroup>();
    const [vehicleFounded, setVehicleFounded] = useState(false);

    const [openErrorMessageDialog, setOpenErrorMessageDialog] = useState(false);
    const [tarrificationProgram, setTarrificationProgram] = useState(null);
    const [isMtplPhoneNumberDialogOpen, setMtplPhoneNumberDialogOpen] = useState(false);

    const [defaultPlateNumber] = useState("")
    const [isRemembered] = useState(false)
    const [defaultTariffied] = useState(false)

    const theme = createTheme({
        palette: {
            secondary: {
                main: '#EF533F'
            }
        }
    });

    const saveTempValues = useCallback((endName: string, value: string) => {
        localStorage.setItem(`${type}_${endName}`, value)
    }, [type])

    const setSelectedViewAndSave = useCallback((value: string) => {
        setSelectedView(value)
        saveTempValues('selectedView', value)
    }, [saveTempValues])
        

    const rememberAll = async () => {
        const memorizedQuotation = JSON.parse(localStorage.getItem(`${type}_quotation`))
        const packages = JSON.parse(localStorage.getItem(`${type}_packages`) || "[]")
        const scenario = await findScenario(scenarioCode)
        const memorizedPackage = JSON.parse(localStorage.getItem(`${type}_selectedPackage`))
        setSelectedPackage(memorizedPackage);
        setSelectedDeductible(memorizedPackage?.deductible);
        const memorizedProgram = JSON.parse(localStorage.getItem(`${type}_selectedProgram`))
        setSelectedProgram(memorizedProgram);
        setQuotation(memorizedQuotation)
        setCurrentScenario(scenario);
        setPackages(packages)
        const memorizedVehicleGroup = localStorage.getItem(`${type}_vehicleGroup`)
        if (memorizedVehicleGroup) {
            setVehicleGroup(VehicleGroup[memorizedVehicleGroup])
        }
        const memorizedSelectedView = localStorage.getItem(`${type}_selectedView`)
        if (memorizedSelectedView) {
            setSelectedView(memorizedSelectedView)
        }
        setLoading(false);
    }

    const fetchData = async () => {
        setQuotation(await newQuotation(salesVariant, scenarioCode, type));
        setCurrentScenario(await findScenario(scenarioCode));
        setLoading(false);
    }

    useEffect(() => {
        if (quotation) {
            localStorage.setItem(`${type}_quotation`, JSON.stringify(quotation))
        }
    }, [quotation])

    useCheckingForMemorizing(type, fetchData, rememberAll, () => setOpenErrorMessageDialog(true))

    // useEffect(() => {
    //     fetchData()
    // }, [])

    const providerValues = useMemo(() => ({
        quotation,
        type,
        setQuotation,
        totalPremium,
        setTotalPremium,
        packages,
        setPackages,
        selectedDeductible,
        setSelectedDeductible,
        selectedPackage,
        setSelectedPackage,
        selectedProgram,
        setSelectedProgram,
        selectedVtplCovered,
        setSelectedVtplCovered,
        selectedVtplSumInsured,
        setSelectedVtplSumInsured,
        selectedVtplPremium,
        setSelectedVtplPremium,
        isQuotationEditable,
        setIsQuotationEditable,
        agreeOption1,
        setAgreeOption1,
        agreeInformOption,
        setAgreeInformOption,
        agreeOption2,
        setAgreeOption2,
        selectedView,
        setSelectedView: setSelectedViewAndSave,
        vehicleTypes,
        setVehicleTypes,
        vehicleGroup,
        setVehicleGroup,
        vehicleFounded,
        setVehicleFounded,
        tarrificationProgram,
        setTarrificationProgram,
        isMtplPhoneNumberDialogOpen,
        setMtplPhoneNumberDialogOpen,
        saveTempValues,
        defaultPlateNumber,
        isRemembered,
        defaultTariffied
    }), [quotation, type, totalPremium, packages, selectedDeductible, selectedPackage, selectedProgram, selectedVtplCovered,
        selectedVtplSumInsured, selectedVtplPremium, isQuotationEditable, agreeOption1, agreeInformOption, agreeOption2, selectedView, vehicleTypes,
        vehicleGroup, vehicleFounded, tarrificationProgram, isMtplPhoneNumberDialogOpen])

    return (
        <div className={'quotationForm'}>

            <ErrorMessageDialog type={ErrorDialogType.ERROR}
                text={strings.errorDefaultMessage}
                open={openErrorMessageDialog}
                onClickListener={() => setOpenErrorMessageDialog(false)} />

            {isLoading ? (
                <ThemeProvider theme={theme}>
                    <Grid container spacing={3}>
                        <Grid item xs={1} />
                        <Grid item xs={5}>
                            <Skeleton variant="rect" height={50} />
                        </Grid>
                        <Grid item xs={6} />

                        <Grid item xs={1} />
                        <Grid item xs={4}>
                            <Skeleton />
                        </Grid>
                        <Grid item xs={7} />

                        <Grid item xs={7} />
                        <Grid item xs={4}>
                            <Grid container spacing={2}>
                                <Grid item xs={6}>
                                    <Skeleton />
                                </Grid>
                                <Grid item xs={6}>
                                    <Skeleton />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={1} />
                    </Grid>
                    <br />
                    <Grid container spacing={5}>
                        <Grid item xs={1} />
                        <Grid item xs={4}>
                            <Grid container spacing={2}>
                                <Grid item xs={7}>
                                    <Skeleton height={20} />
                                    <Skeleton height={20} />
                                </Grid>
                                <Grid item xs={5}></Grid>
                            </Grid>
                            <br />
                            <Skeleton variant="rect" height={50} />
                            <br />
                            <Skeleton />
                            <Skeleton />
                        </Grid>
                        <Grid item xs={2} />
                        <Grid item xs={4}>
                            <Skeleton variant="rect" height={40} />
                            <br />
                            <Skeleton />
                            <Skeleton />
                            <br />
                            <Skeleton />
                            <Skeleton />
                            <Skeleton />
                            <Skeleton />
                            <Skeleton />
                            <br />
                            <Skeleton />
                            <Skeleton />
                            <Skeleton />
                            <Skeleton />
                        </Grid>
                        <Grid item xs={1} />
                    </Grid>
                </ThemeProvider>
            ) : (
                <ThemeProvider theme={theme}>
                    <QuotationContext.Provider value={providerValues}>
                        <QuotationCardContextProvider>
                            <ScenarioComponent scenario={currentScenario} type={type} />
                        </QuotationCardContextProvider>
                    </QuotationContext.Provider>
                </ThemeProvider>
            )}
        </div>
    )
};
